import { FollowUpActionStatus } from '@lmc/ts-common'
import type { FormInstance } from 'antd'
import { useEffect } from 'react'
import type { ActionPlanValue } from 'screens/Residents/screens/ResidentsGoalsScreen/components/ActionPlanInput/ActionPlanInput'
import type { IncidentReportCaseForm } from '../IncidentCaseForm'
import { followUpActionsFieldName } from '../IncidentCaseFormFields'

type UseFollowUpActionsProps = {
  form: FormInstance
  notificationRequired: boolean
  safeguardingRequired: boolean
  initialValues: Partial<IncidentReportCaseForm<string>>
  regulatorNotificationTitle: string
  safeguardingTitle: string
}

const useFollowUpActions = ({
  form,
  notificationRequired,
  safeguardingRequired,
  initialValues,
  regulatorNotificationTitle,
  safeguardingTitle
}: UseFollowUpActionsProps) => {
  const getActionsValue = (): ActionPlanValue<FollowUpActionStatus>[] => {
    return form.getFieldValue(followUpActionsFieldName) || []
  }

  const setActionsValue = (
    newValues: ActionPlanValue<FollowUpActionStatus>[]
  ) => {
    form.setFieldValue(followUpActionsFieldName, newValues)
  }

  const containsItemWithTitle = (title: string) => {
    return Boolean(getActionsValue().find((value) => value.title === title))
  }

  const createActionFromInitialValue = (
    title: string,
    initialAction?: ActionPlanValue<FollowUpActionStatus>
  ): ActionPlanValue<FollowUpActionStatus> => ({
    title,
    status: initialAction?.status || FollowUpActionStatus.notStarted,
    id: undefined,
    canEdit: false,
    canDelete: false,
    assignedUserId: initialAction?.assignedUserId || undefined
  })

  const addOrUpdateAction = (
    title: string,
    action: ActionPlanValue<FollowUpActionStatus>
  ) => {
    const currentActions = getActionsValue()

    if (!containsItemWithTitle(title)) {
      setActionsValue([...currentActions, action])
    } else {
      const updatedActions = currentActions.map((value) =>
        value.title === title ? action : value
      )
      setActionsValue(updatedActions)
    }
  }

  const removeActionByTitle = (title: string) => {
    const filteredActions = getActionsValue().filter(
      (value) => value.title !== title
    )
    setActionsValue(filteredActions)
  }

  // Handle regulator notification
  useEffect(() => {
    const regulatorNotificationActionInitialValue =
      initialValues.followUpActions?.actions.find(
        (action) => action.title === regulatorNotificationTitle
      )

    const regulatorNotificationAction = createActionFromInitialValue(
      regulatorNotificationTitle,
      regulatorNotificationActionInitialValue
    )

    if (notificationRequired) {
      addOrUpdateAction(regulatorNotificationTitle, regulatorNotificationAction)
    } else if (containsItemWithTitle(regulatorNotificationTitle)) {
      removeActionByTitle(regulatorNotificationTitle)
    }
  }, [
    notificationRequired,
    regulatorNotificationTitle,
    initialValues.followUpActions?.actions
  ])

  // Handle safeguarding action
  useEffect(() => {
    const safeguardingActionInitialValue =
      initialValues.followUpActions?.actions.find(
        (action) => action.title === safeguardingTitle
      )

    const safeguardingAction = createActionFromInitialValue(
      safeguardingTitle,
      safeguardingActionInitialValue
    )

    if (safeguardingRequired) {
      addOrUpdateAction(safeguardingTitle, safeguardingAction)
    } else if (containsItemWithTitle(safeguardingTitle)) {
      removeActionByTitle(safeguardingTitle)
    }
  }, [
    safeguardingRequired,
    safeguardingTitle,
    initialValues.followUpActions?.actions
  ])
}

export default useFollowUpActions
